import { Head } from '@inertiajs/react'
import {
  HiArrowTrendingUp,
  HiCheckCircle,
  HiCurrencyDollar,
  HiFire,
  HiQuestionMarkCircle,
  HiUsers,
} from 'react-icons/hi2'
import EventLayout from '../../components/EventLayout'
import ChartCard, { ChartDatum } from '../../components/ChartCard'
import ContactedWithin7DaysCard, {
  ContactedWithin7Days,
} from '../../components/ContactedWithin7DaysCard'
import StagesCard, { StageCount } from '../../components/StagesCard'
import StatCard from '../../components/StatCard'
import TopSalesCard, { TopSalesEntry } from '../../components/TopSalesCard'

type EventStatus = 'upcoming' | 'ongoing' | 'completed'

type PriorityCount = { priority: 'hot' | 'warm' | 'cold'; count: number }

type ChoiceQuestion = {
  id: number
  text: string
  question_type: 'single' | 'multi'
  options: Array<{ id: number; label: string; count: number }>
}

type Props = {
  event: {
    id: number
    title: string
    slug: string
    location: string
    date_range: string
    status: EventStatus
    banner_url: string | null
  }
  events: Array<{ title: string; slug: string }>
  active_tab: 'analytics'
  stats: {
    total_leads: number
    hot_leads: number
    proposal_sent: number
    won: number
  }
  top_sales: TopSalesEntry[]
  stage_counts: StageCount[]
  contacted_within_7_days: ContactedWithin7Days
  lead_priority: PriorityCount[]
  choice_questions: ChoiceQuestion[]
}

const PRIORITY_META: Record<PriorityCount['priority'], { label: string; color: string }> = {
  hot: { label: 'Hot', color: '#E6007E' },
  warm: { label: 'Warm', color: '#F59E0B' },
  cold: { label: 'Cold', color: '#06B6D4' },
}

export default function Analytics({
  event,
  events,
  stats,
  top_sales,
  stage_counts,
  contacted_within_7_days,
  lead_priority,
  choice_questions,
}: Props) {
  const priorityData: ChartDatum[] = lead_priority.map((row) => ({
    label: PRIORITY_META[row.priority].label,
    value: row.count,
    color: PRIORITY_META[row.priority].color,
  }))

  return (
    <>
      <Head title={`${event.title} · Analytics`} />
      <EventLayout event={event} events={events} active="analytics">
        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-5">
            <StatCard
              label="Total Leads"
              value={stats.total_leads}
              icon={<HiUsers className="h-5 w-5" />}
              iconBgClass="bg-[#93D500]/20"
              iconColorClass="text-[#5C8A00]"
            />
            <StatCard
              label="Hot Leads"
              value={stats.hot_leads}
              icon={<HiFire className="h-6 w-6" />}
              iconBgClass="bg-[#F6CCF7]"
              iconColorClass="text-[#A13DA6]"
            />
            <StatCard
              label="Proposal Sent"
              value={stats.proposal_sent}
              icon={<HiCheckCircle className="h-6 w-6" />}
              iconBgClass="bg-[#D6F8FF]"
              iconColorClass="text-[#0098B0]"
            />
            <StatCard
              label="Won"
              value={stats.won}
              icon={<HiCurrencyDollar className="h-6 w-6" />}
              iconBgClass="bg-[#D6D9FF]"
              iconColorClass="text-[#4A52CC]"
            />
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            <TopSalesCard entries={top_sales} />
            <StagesCard entries={stage_counts} />
            <ContactedWithin7DaysCard data={contacted_within_7_days} />
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <ChartCard
              title="Lead Priority"
              icon={<HiArrowTrendingUp className="h-5 w-5" />}
              data={priorityData}
              defaultChart="bar"
            />
            {choice_questions.map((q) => (
              <ChartCard
                key={q.id}
                title={q.text}
                icon={<HiQuestionMarkCircle className="h-5 w-5" />}
                data={q.options.map((opt) => ({ label: opt.label, value: opt.count }))}
                defaultChart="pie"
              />
            ))}
          </div>
        </div>
      </EventLayout>
    </>
  )
}
